import {
  type LexerResult as BaseLexerResult,
  type RegexRule as BaseRegexRule,
  type ReplacementRule as BaseReplacementRule,
  type Rule as BaseRule,
  type StringRule as BaseStringRule,
  type Token as BaseToken,
  createLexer as baseCreateLexer,
} from "leac";
import { TokenizingError } from "./errors";

export { type Options } from "leac";

type Named<
  TBase extends { name: string },
  TTokenType extends string,
> = TBase & { name: TTokenType };

export type Token<TTokenType extends string = string> = Named<
  BaseToken,
  TTokenType
>;

export type LexerRule<TTokenType extends string = string> = Named<
  BaseRule,
  TTokenType
>;
export type LexerStringRule<TTokenType extends string = string> = Named<
  BaseStringRule,
  TTokenType
>;
export type LexerRegexRule<TTokenType extends string = string> = Named<
  BaseRegexRule,
  TTokenType
>;
export type LexerReplacementRule<TTokenType extends string = string> = Named<
  BaseReplacementRule,
  TTokenType
>;

type AnyRule<TTokenType extends string> =
  | LexerRule<TTokenType>
  | LexerStringRule<TTokenType>
  | LexerRegexRule<TTokenType>
  | LexerReplacementRule<TTokenType>;

export type LexerRules<TTokenType extends string = string> = [
  AnyRule<TTokenType>,
  ...AnyRule<TTokenType>[],
];

export type LexerResult<TTokenType extends string = string> = Omit<
  BaseLexerResult,
  "tokens"
> & { tokens: Token<TTokenType>[] };

export type Lexer<TTokenType extends string = string> = (
  str: string,
  offset?: number,
) => LexerResult<TTokenType>;

type ExcludeFirstParameter<T extends unknown[]> = T extends [any, ...infer U]
  ? U
  : never;

type CreateLeacLexerParameters = Parameters<typeof baseCreateLexer>;

type CreateLexerParams<TTokenType extends string> = [
  LexerRules<TTokenType>,
  ...ExcludeFirstParameter<CreateLeacLexerParameters>,
];

export function createLexer<TTokenType extends string>(
  ...args: CreateLexerParams<TTokenType>
) {
  try {
    const lexer = baseCreateLexer(...args);
    return lexer as Lexer<TTokenType>;
  } catch (error: any) {
    throw new TokenizingError(error.message, { cause: error });
  }
}
